interface UserNameData {
  name: string;
  lastUpdated: number;
  expirationDate: number;
}

class UserNameStorageService {
  private readonly STORAGE_KEY = 'aizen_user_name_data';
  private readonly EXPIRATION_DAYS = 20;
  private readonly EXPIRATION_MS = 20 * 24 * 60 * 60 * 1000; // 20 days

  /**
   * Get the stored user name if it exists and hasn't expired
   */
  getUserName(): string | null {
    try {
      if (!this.isStorageAvailable()) {
        return null;
      }

      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const data: UserNameData = JSON.parse(stored);
      
      // Check if expired
      if (this.isExpired(data)) {
        this.clearExpiredData();
        return null;
      }

      return data.name;
    } catch (error) {
      console.error('Error getting user name:', error);
      return null;
    }
  }

  /**
   * Store a user name with expiration
   */
  setUserName(name: string): boolean {
    try {
      if (!this.isStorageAvailable()) {
        return false;
      }

      const trimmedName = name.trim();
      if (!trimmedName) {
        return false;
      }

      const now = Date.now();
      const data: UserNameData = {
        name: trimmedName,
        lastUpdated: now,
        expirationDate: now + this.EXPIRATION_MS
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Error setting user name:', error);
      return false;
    }
  }

  /**
   * Check if stored data is expired
   */
  private isExpired(data?: UserNameData): boolean {
    if (!data) {
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (!stored) return true;
        data = JSON.parse(stored);
      } catch {
        return true;
      }
    }

    return Date.now() > data.expirationDate;
  }

  /**
   * Clear expired data
   */
  clearExpiredData(): void {
    try {
      if (!this.isStorageAvailable()) {
        return;
      }

      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const data: UserNameData = JSON.parse(stored);
      if (this.isExpired(data)) {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error clearing expired data:', error);
    }
  }

  /**
   * Clear all stored data
   */
  clearAllData(): void {
    try {
      if (!this.isStorageAvailable()) {
        return;
      }
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing all data:', error);
    }
  }

  /**
   * Check if localStorage is available
   */
  isStorageAvailable(): boolean {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get days until expiration
   */
  getDaysUntilExpiration(): number {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return 0;

      const data: UserNameData = JSON.parse(stored);
      const timeLeft = data.expirationDate - Date.now();
      return Math.max(0, Math.ceil(timeLeft / (24 * 60 * 60 * 1000)));
    } catch {
      return 0;
    }
  }
}

// Export singleton instance
export const userNameStorageService = new UserNameStorageService(); 